import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Bot,
  CalendarDays,
  Download,
  FileText,
  Gavel,
  Loader2,
  MessageSquare,
  Mic,
  Plus,
  Scale,
  Search,
  Share2,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import CollaborationStrip from '@/components/CollaborationStrip';
import JudgeStrategyWorkspace from '@/components/matter/JudgeStrategyWorkspace';
import MatterWorkspaceContext, { withQueryParams } from '@/components/matter/MatterWorkspaceContext';
import MatterOperationsWorkspace from '@/components/matter/MatterOperationsWorkspace';
import { buildSentinelMatterContext, getMatterDeskRoute, resolveMatterDesk } from '@/lib/matterDesk';
import { buildHtmlExport, buildPlainTextExport, resolveShareUrl, triggerDownload } from '@/lib/shareExport';
import { AppEmptyState, AppPage, AppPageHeader, AppSectionHeader } from '@/components/layout/AppPage';
import { recordJourneyVisit } from '@/lib/chatJourney';

function formatDate(value, options = {}) {
  if (!value) return 'Needs review';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...options,
  });
}

function formatRelativeTime(value) {
  if (!value) return 'Just now';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const diff = Date.now() - parsed.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function compactText(value, max = 180) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
}

function pickTitle(item) {
  if (!item || typeof item !== 'object') return '';
  return compactText(
    item.title
      || item.label
      || item.description
      || item.summary
      || item.content
      || item.event
      || '',
    120,
  );
}

function flattenIssueItems(issuesMap = {}) {
  const sections = [
    { key: 'legalIssues', label: 'Legal issue' },
    { key: 'facts', label: 'Fact' },
    { key: 'disputedFacts', label: 'Dispute' },
    { key: 'missingInformation', label: 'Gap' },
  ];

  return sections.flatMap(({ key, label }) =>
    (issuesMap[key] || []).map((item, index) => ({
      id: item.id || `${key}_${index}`,
      label,
      text: pickTitle(item),
      reviewState: item.reviewState,
      collaboration: item.collaboration,
      owner: item.owner,
    })),
  ).filter((item) => item.text);
}

function buildDocumentLabel(document) {
  return compactText(
    document.title
      || document.originalName
      || document.filename
      || document.fileName
      || 'Matter document',
    120,
  );
}

function buildRecentActivity(workspace) {
  const jollyItems = (workspace.jollySessions || []).slice(0, 2).map((item) => ({
    id: item.session_id || item.title,
    agent: 'Jolly',
    text: compactText(item.title || item.summary || 'Jolly capture added to this matter.', 140),
    updatedAt: item.updated_at || item.created_at,
  }));
  const ainaItems = (workspace.ainaRuns || []).slice(0, 2).map((item) => ({
    id: item.run_id || item.title,
    agent: 'Red Team',
    text: compactText(item.summary || item.title || 'Red-team review updated this matter.', 140),
    updatedAt: item.updated_at || item.created_at,
  }));
  const chatItems = (workspace.matterChats || []).slice(0, 2).map((item) => ({
    id: item.chat_id || item.title,
    agent: 'Chat',
    text: compactText(item.title || item.mode || 'Matter chat updated.', 140),
    updatedAt: item.updated_at || item.created_at,
  }));

  return [...jollyItems, ...ainaItems, ...chatItems]
    .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
    .slice(0, 5);
}

function buildAgentCards({
  workspace,
  matterDesk,
  overviewHref,
  draftHref,
  filesHref,
  reviewHref,
}) {
  const matter = workspace.matter || {};
  const intelligence = workspace.intelligence || {};
  const issueCount = flattenIssueItems(intelligence.issuesMap || {}).length;
  const nextStepCount = (matterDesk.next_steps || []).length;
  const latestJolly = workspace.jollySessions?.[0];
  const latestAina = workspace.ainaRuns?.[0];
  const latestDocument = workspace.documents?.[0];
  const documentCount = workspace.documents?.length || 0;
  const authorityCount = workspace.authorities?.length || 0;

  return [
    {
      key: 'overview',
      title: 'Overview',
      icon: Users,
      tone: 'rgba(14, 116, 144, 0.08)',
      summary: compactText(
        intelligence.overview
          || matterDesk.continuity_summary
          || 'Keep synthesis, hearings, and next steps inside one live matter screen.',
        150,
      ),
      meta: `${issueCount} issue${issueCount === 1 ? '' : 's'} · ${nextStepCount} next step${nextStepCount === 1 ? '' : 's'}`,
      href: overviewHref || getMatterDeskRoute(matterDesk, 'sabha', matter.matter_id ? `/war-room/${matter.matter_id}` : '/war-room'),
      cta: 'Open overview',
    },
    {
      key: 'draft',
      title: 'Draft',
      icon: FileText,
      tone: 'rgba(202, 138, 4, 0.08)',
      summary: latestDocument
        ? compactText(`${buildDocumentLabel(latestDocument)} is ready to continue.`, 150)
        : 'Open drafting without leaving the matter context.',
      meta: documentCount ? `${documentCount} draft${documentCount === 1 ? '' : 's'} linked` : 'No draft linked yet',
      href: draftHref,
      cta: documentCount ? 'Continue draft' : 'Open drafting',
    },
    {
      key: 'files',
      title: 'Files',
      icon: Search,
      tone: 'rgba(59, 130, 246, 0.08)',
      summary: authorityCount
        ? `${authorityCount} grounded source${authorityCount === 1 ? '' : 's'} already support this matter.`
        : 'Upload the live record, briefs, and exhibits before the next move.',
      meta: documentCount ? `${documentCount} file-linked item${documentCount === 1 ? '' : 's'}` : 'No uploads in scope yet',
      href: filesHref,
      cta: 'Open files',
    },
    {
      key: 'jolly',
      title: 'Jolly',
      icon: Mic,
      tone: 'rgba(5, 150, 105, 0.08)',
      summary: latestJolly
        ? compactText(latestJolly.title || latestJolly.summary || 'Reviewed Jolly memory is available.', 150)
        : 'Capture calls, hearing notes, and spoken strategy without leaving the matter.',
      meta: latestJolly ? `${workspace.jollySessions.length} Jolly session${workspace.jollySessions.length === 1 ? '' : 's'} linked` : 'No Jolly session yet',
      href: getMatterDeskRoute(matterDesk, 'jolly', '/jolly'),
      cta: 'Open Jolly',
    },
    {
      key: 'review',
      title: 'Review',
      icon: Bot,
      tone: 'rgba(234, 88, 12, 0.08)',
      summary: latestAina
        ? compactText(latestAina.summary || latestAina.title || 'Recent red-team review is ready.', 150)
        : 'Pressure-test the theory of the matter and surface the weak points before filing.',
      meta: latestAina ? `Last review ${formatRelativeTime(latestAina.created_at || latestAina.updated_at)}` : 'No review run yet',
      href: reviewHref,
      cta: 'Open review',
    },
  ];
}

function SurfaceCard({ title, children, actions = null, soft = false }) {
  return (
    <section
      className="advoq-card"
      style={{
        padding: '18px',
        borderRadius: '24px',
        background: soft ? 'color-mix(in srgb, var(--bg-elevated) 88%, var(--accent-primary) 3%)' : 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px', lineHeight: 1.3 }}>
          {title}
        </h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

function MetricStrip({ items }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="advoq-card"
          style={{
            padding: '18px',
            borderRadius: '20px',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
            {item.label}
          </div>
          <div style={{ color: 'var(--text-primary)', fontSize: '28px', fontWeight: 800, letterSpacing: '-0.04em' }}>
            {item.value}
          </div>
          {item.note ? (
            <div style={{ marginTop: '7px', color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
              {item.note}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function InlineEmptyState({ icon: Icon = Sparkles, title = 'Awaiting matter signal', description, action = null, compact = false }) {
  if (!description && !action) return null;

  return (
    <div className={`flex flex-col items-center justify-center text-center ${compact ? 'py-10' : 'py-16'}`}>
      <Icon className={`${compact ? 'w-10 h-10' : 'w-12 h-12'} text-gray-300 mb-4`} />
      <h3 className={`${compact ? 'text-base' : 'text-lg'} font-medium text-gray-600 mb-2`}>{title}</h3>
      {description ? <p className="text-sm text-gray-400 max-w-xs">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

function MatterCardSkeleton({ count = 3 }) {
  return (
    <div className="animate-pulse grid gap-3">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={`matter-skeleton-${index}`}
          className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-4"
        >
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
        </div>
      ))}
    </div>
  );
}

function WorkspaceLoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`metric-skeleton-${index}`}
            className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-5"
          >
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
            <div className="mt-4 h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            <div className="mt-4 h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
          </div>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <div
            key={`panel-skeleton-${index}`}
            className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-6 space-y-3"
          >
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
          </div>
        ))}
      </div>
    </div>
  );
}

function StackList({ items, empty, renderItem, emptyIcon, emptyTitle, emptyAction, compact = false }) {
  if (!items.length) {
    return (
      <InlineEmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={empty}
        action={emptyAction}
        compact={compact}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {items.map(renderItem)}
    </div>
  );
}

function MatterListCard({ matter, onOpen, label, compact = false }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(matter)}
      className="advoq-card"
      style={{
        width: '100%',
        textAlign: 'left',
        padding: compact ? '14px' : '18px',
        borderRadius: compact ? '18px' : '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '8px' : '10px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <span
              style={{
                padding: '5px 9px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 700,
                color: 'var(--accent-primary)',
                background: 'rgba(15,118,110,0.08)',
              }}
            >
              {label}
            </span>
          </div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: compact ? '15px' : '17px', lineHeight: 1.35 }}>
            {matter.title || matter.name || 'Untitled matter'}
          </h3>
          <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: compact ? '12px' : '13px', lineHeight: 1.55 }}>
            {compactText(
              matter.description
                || matter.summary
                || matter.history_meta
                || matter.court_forum
                || 'Continue work where you left off.',
              compact ? 100 : 150,
            )}
          </p>
        </div>
        <ArrowRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: '2px' }} />
      </div>
      <div style={{ display: 'flex', gap: compact ? '8px' : '12px', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: compact ? '11px' : '12px' }}>
        {matter.history_meta ? <span>{matter.history_meta}</span> : null}
        {matter.court_forum ? <span>{matter.court_forum}</span> : null}
        {matter.latest_activity_at || matter.updated_at ? <span>{formatRelativeTime(matter.latest_activity_at || matter.updated_at)}</span> : null}
      </div>
    </button>
  );
}

function AgentCard({ card, onOpen }) {
  const Icon = card.icon;
  return (
    <div
      className="advoq-card"
      style={{
        padding: '20px',
        borderRadius: '22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        border: '1px solid var(--border-subtle)',
        background: `linear-gradient(180deg, ${card.tone}, var(--bg-surface))`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
          }}
        >
          <Icon size={18} />
        </span>
        <div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '16px' }}>
            {card.title}
          </h3>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
            {card.meta}
          </div>
        </div>
      </div>
      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>
        {card.summary}
      </p>
      <button
        type="button"
        className="app-chat-button"
        onClick={() => onOpen(card.href)}
        style={{ alignSelf: 'flex-start' }}
      >
        {card.cta}
      </button>
    </div>
  );
}

function StrategyList({ items, empty, renderItem, emptyIcon, emptyTitle, emptyAction }) {
  return (
    <StackList
      items={items || []}
      empty={empty}
      renderItem={renderItem}
      emptyIcon={emptyIcon}
      emptyTitle={emptyTitle}
      emptyAction={emptyAction}
      compact
    />
  );
}

function LedgerItem({ icon: Icon, title, description, meta }) {
  return (
    <div
      className="advoq-card"
      style={{
        padding: '14px',
        borderRadius: '18px',
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-elevated)',
        display: 'grid',
        gap: '6px',
      }}
    >
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span style={{ color: 'var(--accent-primary)', marginTop: '2px' }}>
          <Icon size={14} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
            {title}
          </div>
          {description ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
              {description}
            </div>
          ) : null}
          {meta ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.5 }}>
              {meta}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MatterOperatingBoardSection({ board, loading, onOpenOps, onOpenAuthorities, onOpenHearings, onOpenVoiceNotes, onOpenRedTeam }) {
  if (loading && !board) {
    return (
      <SurfaceCard title="Operating board" soft>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)' }}>
          <Loader2 size={16} className="animate-spin" />
          Tightening the matter operating board…
        </div>
      </SurfaceCard>
    );
  }

  if (!board) {
    return null;
  }

  const health = getHealthBadge(board.health_score || 0);
  const authorityCards = (board.authority_ledger?.cards || []).slice(0, 3);
  const issueLedger = (board.issue_ledger || []).slice(0, 4);
  const chronologyLedger = (board.chronology_ledger || []).slice(0, 3);
  const riskRegister = (board.risk_register || []).slice(0, 3);
  const clientRequests = (board.client_requests || []).slice(0, 3);
  const juniorInstructions = (board.junior_instructions || []).slice(0, 3);
  const jollySessions = (board.voice_notes || []).slice(0, 2);
  const readiness = board.hearing_readiness || {};
  const filing = board.filing_readiness || {};

  return (
    <SurfaceCard
      title="Operating board"
      actions={(
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" className="app-chat-button" onClick={onOpenOps}>
            <ShieldCheck size={14} />
            Ops
          </button>
          <button type="button" className="app-chat-button" onClick={onOpenHearings}>
            <Gavel size={14} />
            Hearings
          </button>
          <button type="button" className="app-chat-button" onClick={onOpenAuthorities}>
            <Scale size={14} />
            Authorities
          </button>
        </div>
      )}
      soft
    >
      <div data-testid="matter-operating-board" style={{ display: 'grid', gap: '18px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '6px 10px',
              borderRadius: '999px',
              fontSize: '11px',
              fontWeight: 700,
              color: health.color,
              background: health.background,
              border: `1px solid ${health.border}`,
            }}
          >
            {health.label}
          </span>
          {board.stage ? <span className="app-page-chip">Stage {formatStageLabel(board.stage)}</span> : null}
          {board.priority ? <span className="app-page-chip">Priority {formatStageLabel(board.priority)}</span> : null}
          {board.next_hearing_date ? <span className="app-page-chip">Hearing {formatDate(board.next_hearing_date)}</span> : null}
          {board.court_name ? <span className="app-page-chip">{board.court_name}</span> : null}
        </div>

        <div style={{ color: 'var(--text-primary)', fontSize: '15px', lineHeight: 1.7 }}>
          {board.overview || 'This board distills chronology, issues, authority strength, readiness, and execution tasks for the live matter.'}
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.1fr_0.9fr]" style={{ alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: '14px' }}>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Chronology ledger</div>
              {(chronologyLedger.length ? chronologyLedger : [{ title: 'No chronology entries yet', description: 'Use files, Jolly, or hearing debriefs to tighten the timeline.' }]).map((item, index) => (
                <LedgerItem
                  key={item.id || `${item.title}_${index}`}
                  icon={CalendarDays}
                  title={item.title || 'Chronology item'}
                  description={compactText(item.description || '', 150)}
                  meta={item.date ? formatDate(item.date) : ''}
                />
              ))}
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Issue ledger</div>
              {(issueLedger.length ? issueLedger : [{ title: 'No issue ledger entries yet', description: 'Matter synthesis will sharpen the issues once the file has enough record and authority context.' }]).map((item, index) => (
                <LedgerItem
                  key={item.id || `${item.title}_${index}`}
                  icon={Scale}
                  title={item.title || 'Issue'}
                  description={compactText(item.description || '', 150)}
                  meta={item.ledger || ''}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div
                className="advoq-card"
                style={{ padding: '16px', borderRadius: '18px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}
              >
                <div style={{ color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Hearing readiness
                </div>
                <div style={{ color: 'var(--text-primary)', fontSize: '22px', fontWeight: 700, marginTop: '10px' }}>
                  {readiness.score || 0}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6, marginTop: '6px' }}>
                  {readiness.label || 'No readiness summary yet.'}
                </div>
              </div>
              <div
                className="advoq-card"
                style={{ padding: '16px', borderRadius: '18px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}
              >
                <div style={{ color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Filing readiness
                </div>
                <div style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: 700, marginTop: '10px', lineHeight: 1.5 }}>
                  {filing.bundle_label || filing.bundle_status || 'Scoping'}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6, marginTop: '6px' }}>
                  {filing.target_filing || 'Target filing still needs to be set.'}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '14px' }}>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center' }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Authority intelligence</div>
                {(board.authority_ledger?.count || 0) ? <span className="app-page-chip">{board.authority_ledger.count} cards</span> : null}
              </div>
              {authorityCards.length ? authorityCards.map((item) => (
                <a
                  key={item.authority_id || item.title}
                  href={item.url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="advoq-card"
                  style={{
                    padding: '14px',
                    borderRadius: '18px',
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--bg-elevated)',
                    display: 'grid',
                    gap: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                    {item.title}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {item.verification?.label ? <span className="app-page-chip">{item.verification.label}</span> : null}
                    {item.treatment?.label ? <span className="app-page-chip">{item.treatment.label}</span> : null}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                    {compactText(item.precedential_weight?.label || item.bench_strength?.label || item.docsource || 'Authority card ready for review.', 150)}
                  </div>
                </a>
              )) : (
                <LedgerItem
                  icon={ShieldCheck}
                  title="Authority cards are still thin"
                  description="Refresh authorities from the research lane before the next filing or hearing push."
                />
              )}
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Risk register</div>
              {(riskRegister.length ? riskRegister : [{ title: 'No formal risk entries yet', description: 'Red team findings and procedural blockers will appear here as they are reviewed.' }]).map((item, index) => (
                <LedgerItem
                  key={item.id || `${item.title}_${index}`}
                  icon={AlertTriangle}
                  title={item.title || 'Risk'}
                  description={compactText(item.description || '', 150)}
                  meta={item.severity ? `Severity ${formatStageLabel(item.severity)}` : ''}
                />
              ))}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="app-chat-button" onClick={onOpenRedTeam}>
                  <Bot size={14} />
                  Open review
                </button>
                <button type="button" className="app-chat-button" onClick={onOpenVoiceNotes}>
                  <Mic size={14} />
                  Open Jolly
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Client loop</div>
                {(clientRequests.length ? clientRequests : [{ title: 'No client requests yet', description: 'Client-safe asks and follow-ups will appear once the matter is in motion.' }]).map((item, index) => (
                  <LedgerItem
                    key={item.id || `${item.title}_${index}`}
                    icon={MessageSquare}
                    title={item.title || 'Client request'}
                    description={compactText(item.description || '', 120)}
                    meta={item.status ? formatStageLabel(item.status) : ''}
                  />
                ))}
              </div>
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Junior instructions</div>
                {(juniorInstructions.length ? juniorInstructions : [{ title: 'No junior instructions yet', description: 'Tasked outputs and review queues will appear here.' }]).map((item, index) => (
                  <LedgerItem
                    key={item.id || `${item.title}_${index}`}
                    icon={Users}
                    title={item.title || 'Instruction'}
                    description={compactText(item.description || '', 120)}
                    meta={item.owner || item.status || ''}
                  />
                ))}
              </div>
            </div>

            {jollySessions.length ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Reviewed Jolly sessions</div>
                {jollySessions.map((item) => (
                  <LedgerItem
                    key={item.session_id || item.title}
                    icon={Mic}
                    title={item.title || 'Jolly session'}
                    description={compactText(item.summary || '', 150)}
                    meta={item.updated_at ? formatDate(item.updated_at) : ''}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </SurfaceCard>
  );
}

function buildCreateMatterForm() {
  return {
    title: '',
    client_name: '',
    opposing_party: '',
    court_forum: '',
    description: '',
  };
}

const LIFECYCLE_STAGE_OPTIONS = [
  { value: 'intake', label: 'Intake' },
  { value: 'active', label: 'Active' },
  { value: 'pending_court', label: 'Court' },
  { value: 'stayed', label: 'Stayed' },
  { value: 'settled', label: 'Settled' },
  { value: 'closed', label: 'Closed' },
];

const LIFECYCLE_PRIORITY_OPTIONS = ['urgent', 'high', 'normal', 'low'];

function formatStageLabel(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
}

function buildLifecycleForm(lifecycle, matter = {}) {
  return {
    stage: lifecycle?.stage || 'active',
    priority: lifecycle?.priority || 'normal',
    court_name: lifecycle?.court_name || matter.court_forum || '',
    court_number: lifecycle?.court_number || '',
    case_number: lifecycle?.case_number || matter.case_number || '',
    cnr_number: lifecycle?.cnr_number || '',
    next_hearing_date: lifecycle?.next_hearing_date || matter.next_hearing_date || '',
    last_hearing_date: lifecycle?.last_hearing_date || '',
    filing_date: lifecycle?.filing_date || '',
    limitation_date: lifecycle?.limitation_date || '',
    relief_sought: lifecycle?.relief_sought || '',
    prayer: lifecycle?.prayer || '',
    tags: Array.isArray(lifecycle?.tags) ? lifecycle.tags.join(', ') : '',
  };
}

function getHealthBadge(score) {
  if (score >= 75) {
    return {
      label: `Healthy · ${score}`,
      background: 'rgba(5,150,105,0.12)',
      color: '#047857',
      border: 'rgba(5,150,105,0.18)',
    };
  }
  if (score >= 45) {
    return {
      label: `Watch · ${score}`,
      background: 'rgba(217,119,6,0.12)',
      color: '#b45309',
      border: 'rgba(217,119,6,0.18)',
    };
  }
  return {
    label: `At risk · ${score}`,
    background: 'rgba(220,38,38,0.12)',
    color: '#b91c1c',
    border: 'rgba(220,38,38,0.18)',
  };
}

function WarRoomPage() {
  const { user, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { matterId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const journeyKey = searchParams.get('journey_key') || '';

  const [matters, setMatters] = useState([]);
  const [cases, setCases] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [operatingBoard, setOperatingBoard] = useState(null);
  const [loadingLanding, setLoadingLanding] = useState(false);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [loadingOperatingBoard, setLoadingOperatingBoard] = useState(false);
  const [creatingMatter, setCreatingMatter] = useState(false);
  const [preparingAgents, setPreparingAgents] = useState(false);
  const [savingOperations, setSavingOperations] = useState(false);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [savingLifecycle, setSavingLifecycle] = useState(false);
  const [createForm, setCreateForm] = useState(buildCreateMatterForm);
  const [lifecycleForm, setLifecycleForm] = useState(() => buildLifecycleForm(null, {}));

  const requestedView = searchParams.get('view');
  const activeView = matterId && ['agents', 'bench', 'ops'].includes(requestedView) ? requestedView : 'overview';

  useEffect(() => {
    document.title = 'War Room — Advoq';
  }, []);

  useEffect(() => {
    if (!journeyKey) return;
    recordJourneyVisit(journeyKey, {
      surface: 'SABHA',
      label: 'Sabha',
      href: `${location.pathname}${location.search}`,
    });
  }, [journeyKey, location.pathname, location.search]);

  const setView = (nextView) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextView === 'agents' || nextView === 'bench' || nextView === 'ops') {
      nextParams.set('view', nextView);
    } else {
      nextParams.delete('view');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        ...(options.headers || getAuthHeaders()),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || 'Request failed');
    }
    return data;
  }, [getAuthHeaders]);

  const loadLanding = useCallback(async () => {
    setLoadingLanding(true);
    try {
      const [mattersResult, casesResult] = await Promise.allSettled([
        fetchJson(`${API}/matters`),
        fetchJson(`${API}/cases`),
      ]);

      if (mattersResult.status === 'fulfilled') {
        setMatters(Array.isArray(mattersResult.value) ? mattersResult.value : []);
      } else {
        setMatters([]);
      }

      if (casesResult.status === 'fulfilled') {
        setCases(Array.isArray(casesResult.value) ? casesResult.value : []);
      } else {
        setCases([]);
      }

      if (mattersResult.status !== 'fulfilled') {
        toast.error(mattersResult.reason?.message || 'Unable to load matters');
      }
    } finally {
      setLoadingLanding(false);
    }
  }, [fetchJson]);

  const loadWorkspace = useCallback(async (targetMatterId = matterId) => {
    if (!targetMatterId) return;
    setLoadingWorkspace(true);
    try {
      const workspaceEndpoints = [
        `${API}/cases/${targetMatterId}`,
        `${API}/matters/${targetMatterId}/workspace`,
      ];

      let lastError = null;
      for (const endpoint of workspaceEndpoints) {
        try {
          const payload = await fetchJson(endpoint);
          setWorkspace(payload);
          setLoadingOperatingBoard(true);
          try {
            const boardPayload = await fetchJson(`${API}/matters/${targetMatterId}/operating-board`);
            setOperatingBoard(boardPayload);
          } catch {
            setOperatingBoard(null);
          } finally {
            setLoadingOperatingBoard(false);
          }
          return payload;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('Unable to load matter workspace');
    } catch (error) {
      setWorkspace(null);
      setOperatingBoard(null);
      toast.error(error.message || 'Unable to load matter workspace');
      return null;
    } finally {
      setLoadingWorkspace(false);
    }
  }, [fetchJson, matterId]);

  useEffect(() => {
    if (matterId) {
      loadWorkspace(matterId);
      return;
    }
    setOperatingBoard(null);
    loadLanding();
  }, [loadLanding, loadWorkspace, matterId]);

  useEffect(() => {
    if (!workspace?.matter?.matter_id) return undefined;
    window.sentinelMatterContext = buildSentinelMatterContext(resolveMatterDesk(workspace), workspace);
    return () => { delete window.sentinelMatterContext; };
  }, [workspace]);

  useEffect(() => {
    setLifecycleForm(buildLifecycleForm(workspace?.lifecycle, workspace?.matter || {}));
  }, [workspace?.lifecycle, workspace?.matter]);

  const openMatter = (matter) => {
    const nextMatterId = matter?.matter_id;
    if (!nextMatterId) return;
    navigate(`/war-room/${nextMatterId}`);
  };

  const createMatter = async (event) => {
    event.preventDefault();
    if (!createForm.title.trim()) {
      toast.error('Matter title is required');
      return;
    }

    setCreatingMatter(true);
    try {
      const payload = await fetchJson(`${API}/matters`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createForm),
      });
      toast.success('Matter created');
      setCreateForm(buildCreateMatterForm());
      navigate(`/war-room/${payload.matter_id}`);
    } catch (error) {
      toast.error(error.message || 'Unable to create matter');
    } finally {
      setCreatingMatter(false);
    }
  };

  const navigateToRoute = useCallback((href) => {
    if (!href) return;
    navigate(href);
  }, [navigate]);

  const workspaceMatter = workspace?.matter || {};
  const lifecycle = workspace?.lifecycle || null;
  const matterDesk = useMemo(() => resolveMatterDesk(workspace), [workspace]);
  const judgeProfiles = workspace?.case?.judge_profiles || [];
  const primaryJudge = judgeProfiles[0] || null;
  const intelligence = workspace?.intelligence || {};
  const issues = useMemo(() => flattenIssueItems(intelligence.issuesMap || {}).slice(0, 6), [intelligence.issuesMap]);
  const chronology = useMemo(() => (intelligence.chronology || []).slice(0, 5), [intelligence.chronology]);
  const nextSteps = useMemo(() => {
    const candidateSteps = [
      ...(matterDesk.next_steps || []),
      ...((workspace?.hearingPack?.nextStepChecklist || []).map((item) => pickTitle(item))),
    ];
    return candidateSteps.filter(Boolean).slice(0, 6);
  }, [matterDesk.next_steps, workspace?.hearingPack?.nextStepChecklist]);
  const documents = useMemo(() => (workspace?.documents || []).slice(0, 6), [workspace?.documents]);
  const authorities = useMemo(() => (workspace?.authorities || []).slice(0, 5), [workspace?.authorities]);
  const recentActivity = useMemo(() => buildRecentActivity(workspace || {}), [workspace]);
  const sabhaStrategy = matterDesk?.sabha_strategy || {};
  const reliefNavigator = sabhaStrategy.relief_navigator || [];
  const oppositionMirror = sabhaStrategy.opposition_mirror || [];
  const deadlineRadar = sabhaStrategy.deadline_radar || [];
  const orderToAction = sabhaStrategy.order_to_action || [];
  const filingReadiness = sabhaStrategy.filing_readiness || { items: [] };
  const hearingDay = sabhaStrategy.hearing_day || {};
  const clientUpdate = sabhaStrategy.client_update || {};
  const chamberTasks = sabhaStrategy.chamber_tasks || [];
  const forumPlaybook = sabhaStrategy.forum_playbook || {};
  const instantActions = sabhaStrategy.instant_actions || [];
  const strategySignals = sabhaStrategy.signals || {};
  const operations = matterDesk?.operations || {};
  const lifecycleHealth = getHealthBadge(lifecycle?.health_score || 0);
  const matterTitle = workspaceMatter.title || workspaceMatter.name || workspaceMatter.matter_id || 'Matter';
  const overviewRoute = getMatterDeskRoute(matterDesk, 'sabha', workspaceMatter.matter_id ? `/war-room/${workspaceMatter.matter_id}` : '/war-room');
  const draftRoute = withQueryParams('/documents', {
    matter_id: workspaceMatter.matter_id,
    matter_name: matterTitle,
  });
  const filesRoute = withQueryParams(
    getMatterDeskRoute(
      matterDesk,
      'research',
      workspaceMatter.matter_id ? `/research?matter_id=${encodeURIComponent(workspaceMatter.matter_id)}` : '/research',
    ),
    {
      matter_id: workspaceMatter.matter_id,
      matter_name: matterTitle,
      context_panel: 'files',
    },
  );
  const jollyRoute = withQueryParams(
    getMatterDeskRoute(
      matterDesk,
      'jolly',
      workspaceMatter.matter_id ? `/jolly?matter_id=${encodeURIComponent(workspaceMatter.matter_id)}` : '/jolly',
    ),
    {
      matter_id: workspaceMatter.matter_id,
      matter_name: matterTitle,
    },
  );
  const reviewRoute = getMatterDeskRoute(
    matterDesk,
    'agents',
    workspaceMatter.matter_id ? `/war-room/${workspaceMatter.matter_id}?view=agents` : '/war-room?view=agents',
  );
  const reviewRunRoute = getMatterDeskRoute(
    matterDesk,
    'aina',
    workspaceMatter.matter_id ? `/aina/${workspaceMatter.matter_id}` : '/aina',
  );
  const matterContextItems = [
    {
      key: 'overview',
      badge: nextSteps.length ? String(nextSteps.length) : '',
      onClick: () => navigateToRoute(overviewRoute),
    },
    {
      key: 'draft',
      badge: documents.length ? String(documents.length) : '',
      onClick: () => navigateToRoute(draftRoute),
    },
    {
      key: 'files',
      badge: authorities.length ? String(authorities.length) : '',
      onClick: () => navigateToRoute(filesRoute),
    },
    {
      key: 'jolly',
      badge: workspace?.jollySessions?.length ? String(workspace.jollySessions.length) : '',
      onClick: () => navigateToRoute(jollyRoute),
    },
    {
      key: 'review',
      badge: workspace?.ainaRuns?.length ? String(workspace.ainaRuns.length) : '',
      onClick: () => navigateToRoute(reviewRoute),
    },
  ];
  const activeMatterContextKey = activeView === 'agents' ? 'review' : 'overview';
  const continueRoute = activeView === 'agents'
    ? reviewRoute
    : documents.length
      ? draftRoute
      : workspace?.jollySessions?.length
        ? jollyRoute
        : filesRoute;
  const agentCards = useMemo(() => buildAgentCards({
    workspace: workspace || {},
    matterDesk,
    overviewHref: overviewRoute,
    draftHref: draftRoute,
    filesHref: filesRoute,
    reviewHref: reviewRunRoute,
  }), [draftRoute, filesRoute, matterDesk, overviewRoute, reviewRunRoute, workspace]);

  const overviewMetrics = [
    {
      label: 'Chronology',
      value: chronology.length || '0',
      note: chronology[0] ? pickTitle(chronology[0]) : 'Build from documents, Jolly, or prior matter memory.',
    },
    {
      label: 'Issues',
      value: issues.length || '0',
      note: issues[0]?.text || 'Sharpen the legal issues before opening review.',
    },
    {
      label: 'Next steps',
      value: nextSteps.length || '0',
      note: nextSteps[0] || 'Keep the matter moving with one clear next action.',
    },
    {
      label: 'Documents',
      value: workspace?.documents?.length || '0',
      note: workspace?.documents?.[0] ? buildDocumentLabel(workspace.documents[0]) : 'Unlimited uploads stay attached here.',
    },
  ];

  const resumeCases = useMemo(() => {
    return (cases || [])
      .filter((item) => item.matter_id)
      .slice(0, 4);
  }, [cases]);

  const prepareAllAgents = async () => {
    if (!workspaceMatter.matter_id) return;
    setPreparingAgents(true);
    try {
      await Promise.all([
        fetchJson(`${API}/agent-workspaces/ensure`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            workspace_id: matterDesk.workspace_id || workspace?.agentWorkspace?.workspace_id || '',
            source_agent: 'CHAT_AGENT',
            seed_text: matterDesk.current_objective?.text || workspaceMatter.description || workspaceMatter.title,
            matter_id: workspaceMatter.matter_id,
            matter_name: workspaceMatter.title || workspaceMatter.name || workspaceMatter.matter_id,
          }),
        }),
        fetchJson(`${API}/matters/${workspaceMatter.matter_id}/authorities/refresh`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh: false }),
        }),
        fetchJson(`${API}/matters/${workspaceMatter.matter_id}/hearing-pack/regenerate`, {
          method: 'POST',
          headers: getAuthHeaders(),
        }),
      ]);
      await loadWorkspace(workspaceMatter.matter_id);
      toast.success('Review workspace is ready');
    } catch (error) {
      toast.error(error.message || 'Unable to prepare the shared agent workspace');
    } finally {
      setPreparingAgents(false);
    }
  };

  const openJudgeStrategy = () => setView('bench');

  const saveOperations = useCallback(async (patch) => {
    if (!workspaceMatter.matter_id) return;
    setSavingOperations(true);
    try {
      await fetchJson(`${API}/matters/${workspaceMatter.matter_id}/ops`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
      });
      await loadWorkspace(workspaceMatter.matter_id);
      toast.success('Matter ops saved');
    } catch (error) {
      toast.error(error.message || 'Unable to save matter ops');
    } finally {
      setSavingOperations(false);
    }
  }, [fetchJson, getAuthHeaders, loadWorkspace, workspaceMatter.matter_id]);

  const saveLifecycle = useCallback(async () => {
    if (!workspaceMatter.matter_id) return;
    setSavingLifecycle(true);
    try {
      await fetchJson(`${API}/matters/${workspaceMatter.matter_id}/lifecycle`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...lifecycleForm,
          tags: lifecycleForm.tags
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
        }),
      });
      await loadWorkspace(workspaceMatter.matter_id);
      toast.success('Lifecycle saved');
    } catch (error) {
      toast.error(error.message || 'Unable to save lifecycle');
    } finally {
      setSavingLifecycle(false);
    }
  }, [fetchJson, getAuthHeaders, lifecycleForm, loadWorkspace, workspaceMatter.matter_id]);

  const shareMatterSummary = async () => {
    if (!workspaceMatter.matter_id) return;
    try {
      const payload = await fetchJson(`${API}/share-links`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entity_type: 'MATTER_SUMMARY', entity_id: workspaceMatter.matter_id }),
      });
      const shareUrl = resolveShareUrl(payload, window.location.origin);
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Matter summary link copied');
    } catch (error) {
      toast.error(error.message || 'Unable to create matter summary link');
    }
  };

  const exportMatterSummary = (format) => {
    const title = workspaceMatter.title || workspaceMatter.name || 'Matter summary';
    const objective = matterDesk.current_objective?.text || 'Set the next objective through draft, files, Jolly, or review.';
    const summary = matterDesk.continuity_summary || intelligence.overview || workspaceMatter.description || '';
    const issuesList = issues.map((item) => `- ${item.text}`).join('\n');
    const stepsList = nextSteps.map((item) => `- ${item}`).join('\n');
    const authoritiesList = authorities.map((item) => `- ${item.title}${item.url ? ` (${item.url})` : ''}`).join('\n');
    const content = [
      `# ${title}`,
      '',
      '## Current objective',
      objective,
      '',
      '## Matter summary',
      summary || 'Matter continuity sharpens as documents, authorities, and reviewed Jolly memory accumulate.',
      issuesList ? `\n## Priority issues\n${issuesList}` : '',
      stepsList ? `\n## Next steps\n${stepsList}` : '',
      authoritiesList ? `\n## Verified authorities\n${authoritiesList}` : '',
    ].filter(Boolean).join('\n');
    const payload = format === 'html'
      ? buildHtmlExport({ title, content, author: user?.name || '', sourceLabel: 'Advoq matter summary' })
      : buildPlainTextExport({ title, content, sourceLabel: 'Advoq matter summary' });
    triggerDownload(payload);
    toast.success(format === 'html' ? 'Matter summary exported as HTML' : 'Matter summary exported as text');
  };

  if (!matterId) {
    return (
      <main className="app-main" data-testid="war-room-page" data-war-room-view="landing">
        <AppPage className="app-page--matter-hub">
          <div
            className="matter-hub-landing grid grid-cols-1 gap-5 md:grid-cols-2"
            data-testid="matter-hub-landing"
            style={{ alignItems: 'start' }}
          >
            <div className="matter-hub-landing__stack">
              <SurfaceCard title="Open a new matter" soft>
                <form onSubmit={createMatter} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <input
                      className="advoq-input"
                      placeholder="Matter title"
                      value={createForm.title}
                      onChange={(event) => setCreateForm((current) => ({ ...current, title: event.target.value }))}
                    />
                    <input
                      className="advoq-input"
                      placeholder="Client or primary party"
                      value={createForm.client_name}
                      onChange={(event) => setCreateForm((current) => ({ ...current, client_name: event.target.value }))}
                    />
                    <input
                      className="advoq-input"
                      placeholder="Opposing party"
                      value={createForm.opposing_party}
                      onChange={(event) => setCreateForm((current) => ({ ...current, opposing_party: event.target.value }))}
                    />
                    <input
                      className="advoq-input"
                      placeholder="Court or forum"
                      value={createForm.court_forum}
                      onChange={(event) => setCreateForm((current) => ({ ...current, court_forum: event.target.value }))}
                    />
                  </div>
                  <textarea
                    className="advoq-input"
                    rows={3}
                    placeholder="What is this matter about, and what needs to happen next?"
                    value={createForm.description}
                    onChange={(event) => setCreateForm((current) => ({ ...current, description: event.target.value }))}
                    style={{ resize: 'vertical' }}
                  />
                  <button type="submit" className="advoq-btn-primary" disabled={creatingMatter} style={{ justifyContent: 'center' }}>
                    {creatingMatter ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Create matter
                  </button>
                </form>
              </SurfaceCard>

              <SurfaceCard title="Previous case continuity">
                {loadingLanding && !resumeCases.length ? (
                  <MatterCardSkeleton count={2} />
                ) : resumeCases.length ? (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {resumeCases.map((record) => (
                      <MatterListCard key={record.matter_id} matter={record} onOpen={openMatter} label={record.history_meta || 'Previous case'} compact />
                    ))}
                  </div>
                ) : (
                  <StackList
                    items={[]}
                    empty="Bring a prior matter back in when you want chronology, strategy, and hearing context to resume without rebuilding everything."
                    emptyIcon={Scale}
                    emptyTitle="No previous matters yet"
                    renderItem={() => null}
                  />
                )}
              </SurfaceCard>
            </div>

            <div className="matter-hub-landing__stack">
              <SurfaceCard
                title="Continue a matter"
                actions={(
                  <button type="button" className="app-chat-button" onClick={loadLanding} disabled={loadingLanding}>
                    {loadingLanding ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    Reload list
                  </button>
                )}
              >
                {loadingLanding && matters.length === 0 ? (
                  <MatterCardSkeleton count={4} />
                ) : matters.slice(0, 6).length ? (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {matters.slice(0, 6).map((matter) => (
                      <MatterListCard key={matter.matter_id} matter={matter} onOpen={openMatter} label="Matter" compact />
                    ))}
                  </div>
                ) : (
                  <StackList
                    items={[]}
                    empty="Create your first matter to keep documents, Jolly, research, and strategy in one shared case file."
                    emptyIcon={Scale}
                    emptyTitle="Add a matter to begin analysis"
                    renderItem={() => null}
                  />
                )}
              </SurfaceCard>
            </div>
          </div>
        </AppPage>
      </main>
    );
  }

  return (
    <main className="app-main" data-testid="war-room-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Matter"
          title={matterTitle}
          description={compactText(
            intelligence.overview
              || matterDesk.continuity_summary
              || workspaceMatter.description
              || 'Keep hearings, draft work, files, Jolly, and review in one operating screen.',
            140,
          )}
          actions={(
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" className="app-chat-button" onClick={() => navigate('/war-room')}>
                <ArrowLeft size={14} />
                All matters
              </button>
            </div>
          )}
          meta={(
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {(workspace?.case?.history_meta || workspaceMatter.court_forum || workspaceMatter.case_number) ? (
                <span className="app-page-chip">{workspace?.case?.history_meta || workspaceMatter.court_forum || workspaceMatter.case_number}</span>
              ) : null}
              {lifecycle?.stage ? (
                <span className="app-page-chip">Lifecycle {formatStageLabel(lifecycle.stage)}</span>
              ) : null}
              {(lifecycle?.next_hearing_date || workspaceMatter.next_hearing_date) ? (
                <span className="app-page-chip">Hearing {formatDate(lifecycle?.next_hearing_date || workspaceMatter.next_hearing_date)}</span>
              ) : null}
              {lifecycle?.health_score !== undefined ? (
                <span className="app-page-chip">Health {lifecycle.health_score}</span>
              ) : null}
              {matterDesk.selected_context?.slice(0, 2).map((item) => (
                <span key={`${item.type}_${item.label}`} className="app-page-chip">
                  {item.label}: {item.value}
                </span>
              ))}
            </div>
          )}
        />

        {loadingWorkspace ? (
          <SurfaceCard title="Loading matter workspace">
            <WorkspaceLoadingSkeleton />
          </SurfaceCard>
        ) : null}

        {!loadingWorkspace && workspace ? (
          <MatterWorkspaceContext
            matterName={matterTitle}
            matterId={workspaceMatter.matter_id}
            workspaceId={matterDesk.workspace_id}
            objective={matterDesk.current_objective?.text || 'Pick up the next move without leaving this matter.'}
            items={matterContextItems}
            activeKey={activeMatterContextKey}
            compact={activeView !== 'overview'}
            primaryAction={activeView === 'overview' ? {
              label: 'Continue',
              icon: ArrowRight,
              onClick: () => navigateToRoute(continueRoute),
              testId: 'matter-workspace-continue',
            } : null}
            secondaryAction={activeView === 'overview' ? {
              label: 'Export',
              icon: Download,
              onClick: () => exportMatterSummary('txt'),
              testId: 'matter-workspace-export',
            } : null}
            testId="matter-workspace-context"
          />
        ) : null}

        {!loadingWorkspace && !workspace ? (
          <AppEmptyState
            icon={FileText}
            title="Matter not found"
            description="This matter could not be loaded. Try returning to the matter list and opening it again."
            action={(
              <button type="button" className="advoq-btn-primary" onClick={() => navigate('/war-room')}>
                Back to matters
              </button>
            )}
          />
        ) : null}

        {!loadingWorkspace && workspace && activeView === 'overview' ? (
          <div data-testid="matter-workspace-overview" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <MetricStrip items={overviewMetrics} />

            <SurfaceCard
              title="Lifecycle"
              actions={(
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span
                    style={{
                      padding: '6px 10px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: lifecycleHealth.color,
                      background: lifecycleHealth.background,
                      border: `1px solid ${lifecycleHealth.border}`,
                    }}
                  >
                    {lifecycleHealth.label}
                  </span>
                  <button type="button" className="app-chat-button" onClick={() => setLifecycleOpen((open) => !open)}>
                    {lifecycleOpen ? 'Collapse' : 'Expand'}
                  </button>
                </div>
              )}
              soft
            >
              <div style={{ display: 'grid', gap: '16px' }}>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Stage
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>
                      {formatStageLabel(lifecycleForm.stage)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      CNR number
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {lifecycleForm.cnr_number || 'Not linked yet'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Next hearing
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {lifecycleForm.next_hearing_date ? formatDate(lifecycleForm.next_hearing_date) : 'No date yet'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Case number
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {lifecycleForm.case_number || 'Pending'}
                    </div>
                  </div>
                </div>

                {lifecycleOpen ? (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {LIFECYCLE_STAGE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className="app-chat-button"
                          onClick={() => setLifecycleForm((current) => ({ ...current, stage: option.value }))}
                          style={lifecycleForm.stage === option.value ? { background: 'rgba(15,118,110,0.08)', color: 'var(--accent-primary)' } : null}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <input
                        className="advoq-input"
                        placeholder="Court name"
                        value={lifecycleForm.court_name}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, court_name: event.target.value }))}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Court number"
                        value={lifecycleForm.court_number}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, court_number: event.target.value }))}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Case number"
                        value={lifecycleForm.case_number}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, case_number: event.target.value }))}
                      />
                      <input
                        className="advoq-input"
                        placeholder="CNR number"
                        value={lifecycleForm.cnr_number}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, cnr_number: event.target.value }))}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Next hearing date"
                        value={lifecycleForm.next_hearing_date}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, next_hearing_date: event.target.value }))}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Last hearing date"
                        value={lifecycleForm.last_hearing_date}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, last_hearing_date: event.target.value }))}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Filing date"
                        value={lifecycleForm.filing_date}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, filing_date: event.target.value }))}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Limitation date"
                        value={lifecycleForm.limitation_date}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, limitation_date: event.target.value }))}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <select
                        className="advoq-input"
                        value={lifecycleForm.priority}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, priority: event.target.value }))}
                      >
                        {LIFECYCLE_PRIORITY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {formatStageLabel(option)}
                          </option>
                        ))}
                      </select>
                      <input
                        className="advoq-input"
                        placeholder="Tags, comma separated"
                        value={lifecycleForm.tags}
                        onChange={(event) => setLifecycleForm((current) => ({ ...current, tags: event.target.value }))}
                      />
                    </div>

                    <textarea
                      className="advoq-input"
                      rows={3}
                      placeholder="Relief sought"
                      value={lifecycleForm.relief_sought}
                      onChange={(event) => setLifecycleForm((current) => ({ ...current, relief_sought: event.target.value }))}
                      style={{ resize: 'vertical' }}
                    />

                    <textarea
                      className="advoq-input"
                      rows={3}
                      placeholder="Prayer"
                      value={lifecycleForm.prayer}
                      onChange={(event) => setLifecycleForm((current) => ({ ...current, prayer: event.target.value }))}
                      style={{ resize: 'vertical' }}
                    />

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                        Health score reacts to recent activity, hearing pressure, open review risks, and whether the file has a CNR number.
                      </div>
                      <button type="button" className="advoq-btn-primary" onClick={saveLifecycle} disabled={savingLifecycle}>
                        {savingLifecycle ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                        Save lifecycle
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </SurfaceCard>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2" style={{ alignItems: 'start' }}>
              <SurfaceCard
                title="Matter summary"
                actions={(
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="button" className="app-chat-button" onClick={() => navigateToRoute(getMatterDeskRoute(matterDesk, 'chat', '/chat'))}>
                      <MessageSquare size={14} />
                      Chat
                    </button>
                    <button type="button" className="app-chat-button" onClick={() => navigateToRoute(getMatterDeskRoute(matterDesk, 'research', '/research'))}>
                      <Search size={14} />
                      Files
                    </button>
                    <button type="button" className="app-chat-button" onClick={() => navigateToRoute(jollyRoute)}>
                      <Mic size={14} />
                      Jolly
                    </button>
                    <button type="button" className="app-chat-button" onClick={() => navigateToRoute(reviewRunRoute)}>
                      <Bot size={14} />
                      Review
                    </button>
                    <button type="button" className="app-chat-button" onClick={shareMatterSummary}>
                      <Share2 size={14} />
                      Share
                    </button>
                    <button type="button" className="app-chat-button" onClick={() => exportMatterSummary('txt')}>
                      <Download size={14} />
                      Export
                    </button>
                  </div>
                )}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Current objective
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '16px', lineHeight: 1.65 }}>
                      {matterDesk.current_objective?.text || 'Set the next objective through draft, files, Jolly, or review.'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Continuity
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
                      {matterDesk.continuity_summary || 'Matter continuity sharpens as documents, authorities, and reviewed Jolly memory accumulate.'}
                    </div>
                  </div>
                </div>
              </SurfaceCard>

              <SurfaceCard
                title="Judge strategy"
                actions={(
                  <button type="button" className="app-chat-button" onClick={openJudgeStrategy}>
                    <Gavel size={14} />
                    {judgeProfiles.length ? 'Open bench prep' : 'Set up bench prep'}
                  </button>
                )}
                soft
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
                    {judgeProfiles.length
                      ? 'Bench prep stays in the matter flow. Keep it optional, restrained, and attached to the file instead of buried in setup.'
                      : 'Bench prep is available directly from this matter when you need it. Search a real judge, infer context from matter files, or create a simulated judge for rehearsal.'}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <span className="app-page-chip">{judgeProfiles.length} active judge {judgeProfiles.length === 1 ? 'agent' : 'agents'}</span>
                    <span className="app-page-chip">Real, matter-linked, or simulated</span>
                    <span className="app-page-chip">Bench prep with restraint</span>
                  </div>
                  {primaryJudge ? (
                    <div style={{ borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', padding: '14px' }}>
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                        {primaryJudge.name}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                        {primaryJudge.type === 'verified'
                          ? 'Real judge-linked analysis'
                          : primaryJudge.type === 'matter_linked'
                            ? 'Matter-linked context'
                            : 'Simulated judge'}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65, marginTop: '8px' }}>
                        {compactText(
                          primaryJudge.short_description
                            || primaryJudge.temperament
                            || 'Bench prep is already attached to this matter.',
                          140,
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </SurfaceCard>

              <SurfaceCard title="Trusted grounding" soft>
                <StackList
                  items={authorities}
                  empty="No verified Indian Kanoon authority is attached yet. Use review or files to ground the matter before the next major move."
                  renderItem={(authority, index) => (
                    <div key={`${authority.url || authority.title}_${index}`} style={{ paddingBottom: index < authorities.length - 1 ? '12px' : 0, borderBottom: index < authorities.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <ShieldCheck size={14} style={{ color: 'var(--accent-primary)' }} />
                        <a href={authority.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, textDecoration: 'none' }}>
                          {authority.title}
                        </a>
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                        {compactText(authority.summary || authority.snippet || authority.docsource || 'Verified Indian Kanoon authority', 120)}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>
            </div>

              <MatterOperatingBoardSection
              board={operatingBoard}
              loading={loadingOperatingBoard}
              onOpenOps={() => setView('ops')}
              onOpenAuthorities={() => navigate('/authorities')}
              onOpenHearings={() => navigate('/hearings')}
              onOpenVoiceNotes={() => navigateToRoute(jollyRoute)}
              onOpenRedTeam={() => navigateToRoute(reviewRunRoute)}
            />

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2" style={{ alignItems: 'start' }}>
              <SurfaceCard
                title="Matter synthesis"
                actions={(
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="button" className="app-chat-button" onClick={() => setView('ops')}>
                      <ShieldCheck size={14} />
                      Matter ops
                    </button>
                    <button type="button" className="app-chat-button" onClick={() => navigateToRoute(getMatterDeskRoute(matterDesk, 'agents', `/war-room/${workspaceMatter.matter_id}?view=agents`))}>
                      <Sparkles size={14} />
                      Review
                    </button>
                    <button type="button" className="app-chat-button" onClick={() => navigateToRoute(getMatterDeskRoute(matterDesk, 'bench', `/war-room/${workspaceMatter.matter_id}?view=bench`))}>
                      <Gavel size={14} />
                      Bench prep
                    </button>
                  </div>
                )}
                soft
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '16px', lineHeight: 1.7 }}>
                    {sabhaStrategy.headline || 'Sabha synthesis should keep the matter anchored to the next decisive move, the live issue, and the cleanest available record.'}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {forumPlaybook.label ? <span className="app-page-chip">{forumPlaybook.label}</span> : null}
                    {filingReadiness.label ? <span className="app-page-chip">{filingReadiness.label}</span> : null}
                    {strategySignals.authorities ? <span className="app-page-chip">{strategySignals.authorities} authority{strategySignals.authorities === 1 ? '' : 'ies'}</span> : null}
                    {strategySignals.voice_memory ? <span className="app-page-chip">{strategySignals.voice_memory} Jolly capture{strategySignals.voice_memory === 1 ? '' : 's'}</span> : null}
                  </div>
                  <StrategyList
                    items={instantActions}
                    empty="No instant matter-synthesis actions are highlighted yet."
                    renderItem={(item, index) => (
                      <div key={`${item}_${index}`} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <ArrowRight size={15} style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                          {item}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </SurfaceCard>

              <SurfaceCard title="Forum playbook">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>
                    {forumPlaybook.label || workspaceMatter.court_forum || 'Current forum'}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
                    {forumPlaybook.approach || 'Sabha synthesis should tailor the matter strategy to the actual forum, the relief the court can grant today, and the proof already in hand.'}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {(forumPlaybook.lanes || []).map((item) => (
                      <span key={item} className="app-page-chip">{item}</span>
                    ))}
                  </div>
                </div>
              </SurfaceCard>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <SurfaceCard title="Relief navigator">
                <StrategyList
                  items={reliefNavigator}
                  empty="No relief lane has been synthesized yet."
                  renderItem={(item, index) => (
                    <div key={`${item.label}_${index}`} style={{ display: 'grid', gap: '8px', paddingBottom: index < reliefNavigator.length - 1 ? '12px' : 0, borderBottom: index < reliefNavigator.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                          {item.label}
                        </div>
                        {item.fit_label ? <span className="app-page-chip">{item.fit_label}</span> : null}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                        {item.why_now}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                        Next move: {item.next_move}
                      </div>
                      {(item.blockers || []).length ? (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {item.blockers.map((blocker) => (
                            <span key={blocker} className="app-page-chip">{blocker}</span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard title="Opposition mirror">
                <StrategyList
                  items={oppositionMirror}
                  empty="No key opposition attack is highlighted yet."
                  renderItem={(item, index) => (
                    <div key={`${item.title}_${index}`} style={{ display: 'grid', gap: '6px', paddingBottom: index < oppositionMirror.length - 1 ? '12px' : 0, borderBottom: index < oppositionMirror.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                          {item.title}
                        </div>
                        {item.risk_label ? <span className="app-page-chip">{item.risk_label}</span> : null}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                        {item.response}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard title="Deadline radar">
                <StrategyList
                  items={deadlineRadar}
                  empty="No deadline or urgency lane is highlighted yet."
                  renderItem={(item, index) => (
                    <div key={`${item.title}_${index}`} style={{ display: 'grid', gap: '6px', paddingBottom: index < deadlineRadar.length - 1 ? '12px' : 0, borderBottom: index < deadlineRadar.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                          {item.title}
                        </div>
                        {item.urgency_label ? <span className="app-page-chip">{item.urgency_label}</span> : null}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                        {item.detail}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard title="Filing readiness">
                <div style={{ display: 'grid', gap: '12px' }}>
                  {filingReadiness.label ? (
                    <div style={{ display: 'inline-flex' }}>
                      <span className="app-page-chip">{filingReadiness.label}</span>
                    </div>
                  ) : null}
                  <StrategyList
                    items={filingReadiness.items || []}
                    empty="No filing readiness signal yet."
                    renderItem={(item) => (
                      <div key={item.label} style={{ display: 'grid', gap: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                          <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>
                            {item.label}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                            {item.status}
                          </div>
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                          {item.detail}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </SurfaceCard>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2" style={{ alignItems: 'start' }}>
              <SurfaceCard title="Hearing day mode" soft>
                <div style={{ display: 'grid', gap: '14px' }}>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Opening
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px', lineHeight: 1.7 }}>
                      {hearingDay.opening || 'Use Sabha synthesis to lock a narrow opening line before the next hearing-facing move.'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Anchors
                    </div>
                    <StrategyList
                      items={hearingDay.anchors || []}
                      empty="No hearing anchors yet."
                      renderItem={(item, index) => (
                        <div key={`${item}_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                          {item}
                        </div>
                      )}
                    />
                  </div>
                  {hearingDay.hardest_question ? (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Hardest question:</strong> {hearingDay.hardest_question}
                    </div>
                  ) : null}
                  {hearingDay.fallback ? (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Fallback:</strong> {hearingDay.fallback}
                    </div>
                  ) : null}
                </div>
              </SurfaceCard>

              <SurfaceCard title="Client translation">
                <div style={{ display: 'grid', gap: '14px' }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '15px', lineHeight: 1.7 }}>
                    {clientUpdate.headline || 'Sabha synthesis can turn strategy into a clean client-facing update once the matter spine is tighter.'}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
                    {clientUpdate.summary || 'Keep the client update narrow: where the matter stands, what the risk is, and what support the team needs next.'}
                  </div>
                  <StrategyList
                    items={clientUpdate.client_actions || []}
                    empty="No client-facing action is highlighted yet."
                    renderItem={(item, index) => (
                      <div key={`${item}_${index}`} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <MessageSquare size={14} style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                          {item}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </SurfaceCard>

              <SurfaceCard title="Chamber taskboard">
                <StrategyList
                  items={chamberTasks}
                  empty="No chamber tasks are highlighted yet."
                  renderItem={(item, index) => (
                    <div key={`${item.owner}_${item.title}_${index}`} style={{ display: 'grid', gap: '6px', paddingBottom: index < chamberTasks.length - 1 ? '12px' : 0, borderBottom: index < chamberTasks.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                          {item.title}
                        </div>
                        {item.owner ? <span className="app-page-chip">{item.owner}</span> : null}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                        {item.detail}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>
            </div>

            {orderToAction.length ? (
              <SurfaceCard title="Order to action">
                <StrategyList
                  items={orderToAction}
                  empty=""
                  renderItem={(item, index) => (
                    <div key={`${item.title}_${index}`} style={{ display: 'grid', gap: '6px', paddingBottom: index < orderToAction.length - 1 ? '12px' : 0, borderBottom: index < orderToAction.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                        {item.title}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                        {item.action}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>
            ) : null}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <SurfaceCard title="Chronology">
                <StackList
                  items={chronology}
                  empty="No chronology has been assembled yet."
                  renderItem={(item, index) => (
                    <div key={item.id || `${item.title}_${index}`} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <CalendarDays size={15} style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                          {pickTitle(item)}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                          {item.date ? formatDate(item.date) : 'Undated'}
                        </div>
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard title="Issues">
                <StackList
                  items={issues}
                  empty="No issues map is ready yet."
                  renderItem={(item) => (
                    <div key={item.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', width: '100%' }}>
                        <Scale size={15} style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                        <div>
                          <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                            {item.text}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                            {item.label}
                          </div>
                        </div>
                      </div>
                      <div style={{ width: '100%' }}>
                        <CollaborationStrip
                          matterId={workspaceMatter.matter_id}
                          targetType="matter_item"
                          targetId={item.id}
                          targetLabel={item.text}
                          initialState={item.collaboration || { review_state: item.reviewState, owner: item.owner }}
                          fallbackReviewState={item.reviewState || 'new'}
                          compact
                        />
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard title="Next steps">
                <StackList
                  items={nextSteps}
                  empty="No next steps have been captured yet."
                  renderItem={(item, index) => (
                    <div key={`${item}_${index}`} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <ArrowRight size={15} style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.65 }}>
                        {item}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard title="Documents">
                <StackList
                  items={documents}
                  empty="No uploaded matter documents yet."
                  renderItem={(document, index) => (
                    <div key={document.file_id || `${document.title}_${index}`} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <FileText size={15} style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.5 }}>
                          {buildDocumentLabel(document)}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                          {formatRelativeTime(document.updatedAt || document.createdAt)}
                        </div>
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2" style={{ alignItems: 'start' }}>
              <SurfaceCard title="Recent matter activity">
                <StackList
                  items={recentActivity}
                  empty="No recent activity across chat, Jolly, or review yet."
                  renderItem={(item) => (
                    <div key={`${item.agent}_${item.id}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>
                          {item.agent}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65, marginTop: '4px' }}>
                          {item.text}
                        </div>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(item.updatedAt)}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard
                title="Need all agents together?"
                actions={(
                  <button type="button" className="advoq-btn-primary" onClick={() => setView('agents')}>
                    <Bot size={14} />
                    Open review
                  </button>
                )}
                soft
              >
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.8 }}>
                  The default workspace stays calm. When you need to move faster, open one coordinated review mode that keeps draft, files, Jolly, and Sabha synthesis aligned around the same matter context.
                </p>
              </SurfaceCard>
            </div>
          </div>
        ) : null}

        {!loadingWorkspace && workspace && activeView === 'bench' ? (
          <div data-testid="matter-judge-strategy-view" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <JudgeStrategyWorkspace
              workspace={workspace}
              user={user}
              getAuthHeaders={getAuthHeaders}
              reloadWorkspace={loadWorkspace}
            />
          </div>
        ) : null}

        {!loadingWorkspace && workspace && activeView === 'ops' ? (
          <MatterOperationsWorkspace
            matterId={workspaceMatter.matter_id}
            operations={operations}
            saving={savingOperations}
            onSave={saveOperations}
          />
        ) : null}

        {!loadingWorkspace && workspace && activeView === 'agents' ? (
          <div data-testid="matter-agents-view" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SurfaceCard
              title="Review"
              actions={(
                <button type="button" className="advoq-btn-primary" onClick={prepareAllAgents} disabled={preparingAgents} style={{ justifyContent: 'center' }}>
                  {preparingAgents ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Prepare coordinated review
                </button>
              )}
              soft
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2" style={{ alignItems: 'start' }}>
                <div>
                  <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '15px', lineHeight: 1.8 }}>
                    This review lane keeps drafting, files, Jolly, and red-team pressure testing aligned inside the same matter context.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
                    <span className="app-page-chip">Matter: {workspaceMatter.title || workspaceMatter.name || workspaceMatter.matter_id}</span>
                    {matterDesk.workspace_id ? <span className="app-page-chip">Shared workspace {matterDesk.workspace_id}</span> : null}
                    {matterDesk.current_objective?.text ? <span className="app-page-chip">Objective in sync</span> : null}
                  </div>
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Current objective</strong>
                  <div style={{ marginTop: '8px' }}>
                    {matterDesk.current_objective?.text || 'No clear objective captured yet. Prepare the run to refresh shared context.'}
                  </div>
                </div>
              </div>
            </SurfaceCard>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {agentCards.map((card) => (
                <AgentCard key={card.key} card={card} onOpen={navigateToRoute} />
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2" style={{ alignItems: 'start' }}>
              <SurfaceCard title="Shared next moves">
                <StackList
                  items={nextSteps}
                  empty="No shared next moves yet."
                  renderItem={(item, index) => (
                    <div key={`${item}_${index}`} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <ArrowRight size={15} style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.7 }}>
                        {item}
                      </div>
                    </div>
                  )}
                />
              </SurfaceCard>

              <SurfaceCard title="Grounding already attached">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
                    <FileText size={15} />
                    {workspace?.documents?.length || 0} document{workspace?.documents?.length === 1 ? '' : 's'}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
                    <Mic size={15} />
                    {workspace?.jollySessions?.length || 0} Jolly session{workspace?.jollySessions?.length === 1 ? '' : 's'}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
                    <ShieldCheck size={15} />
                    {workspace?.authorities?.length || 0} verified authorit{workspace?.authorities?.length === 1 ? 'y' : 'ies'}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
                    <Gavel size={15} />
                    {issues.length || 0} mapped issue{issues.length === 1 ? '' : 's'}
                  </div>
                </div>
              </SurfaceCard>
            </div>
          </div>
        ) : null}
      </AppPage>
    </main>
  );
}

export default WarRoomPage;
